/* OBFUSCATED CODE - INTELLECTUAL PROPERTY PROTECTED */
'use client';
import { useEffect, useRef, useCallback } from 'react';
import { getTranslation } from './i18n';
import { getUserPlan } from '../../../lib/pb';
const SAVE_DEBOUNCE_MS = 5000;
const CHUNK_SIZE = 800;
export function useCanvasPersistence({ canvasId, engineReady, lang, isGlobal = false }) {
    const COLS = isGlobal ? 10 : 5;
    const ROWS = 2;
    const CANVAS_KEY = isGlobal ? 'sw:canvas_wall' : 'sw:canvas_spray';
    const CANVAS_CHUNKS_KEY = isGlobal ? 'sw:canvas_chunks_wall' : 'sw:canvas_chunks';
    const debounceTimerRef = useRef(null);
    const undoIndexRef = useRef(-1);
    const historyRef = useRef([]);
    const initialStateRef = useRef(null);
    const pendingDirtyChunksRef = useRef(new Set());
    const currentLoadedChunksRef = useRef({});
    const lastUpdateTimestampRef = useRef(0);
    const UPDATE_COOLDOWN_MS = 15000;
    const SAVE_COOLDOWN_MS = isGlobal ? 10000 : 5000;
    useEffect(() => {
        if (!engineReady) return;
        async function loadInitial() {
            try {
                localStorage.removeItem(CANVAS_KEY);
                localStorage.removeItem(CANVAS_CHUNKS_KEY);
                const { getCanvasHistory } = await import('./pocketbaseSync');
                const history = await getCanvasHistory(isGlobal);
                historyRef.current = history;
                undoIndexRef.current = -1;
                if (history && history.length > 0) {
                    const latest = history[0];
                    const canvas = document.getElementById(canvasId);
                    if (!canvas) return;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    const chunksSource = latest.chunksData || latest.activeChunks;
                    if (chunksSource) {
                        currentLoadedChunksRef.current = { ...chunksSource };
                        Object.keys(chunksSource).forEach(chunkId => {
                            const [c, r] = chunkId.split('_').map(Number);
                            const img = new window.Image();
                            img.crossOrigin = "anonymous";
                            img.onload = () => {
                                ctx.drawImage(img, c * CHUNK_SIZE, r * CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE);
                                if (window.updateMinimap) window.updateMinimap();
                            };
                            img.src = chunksSource[chunkId];
                        });
                    } else if (latest.url) {
                        const img = new window.Image();
                        img.crossOrigin = "anonymous";
                        img.onload = () => {
                            ctx.drawImage(img, 0, 0);
                            if (window.updateMinimap) window.updateMinimap();
                        };
                        img.src = latest.url;
                        currentLoadedChunksRef.current = {};
                    }
                }
            } catch (e) {
            }
        }
        loadInitial();
    }, [canvasId, engineReady, isGlobal]);
    const getGluedImage = useCallback(async () => {
        const canvas = document.getElementById(canvasId);
        if (canvas) return canvas.toDataURL('image/png');
        const glueCanvas = document.createElement('canvas');
        glueCanvas.width = COLS * CHUNK_SIZE;
        glueCanvas.height = ROWS * CHUNK_SIZE;
        const gCtx = glueCanvas.getContext('2d');
        try {
            const chunksData = currentLoadedChunksRef.current;
            const promises = Object.keys(chunksData).map(chunkId => {
                return new Promise(resolve => {
                    const [c, r] = chunkId.split('_').map(Number);
                    const img = new window.Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => {
                        gCtx.drawImage(img, c * CHUNK_SIZE, r * CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE);
                        resolve();
                    };
                    img.src = chunksData[chunkId];
                });
            });
            await Promise.all(promises);
            return glueCanvas.toDataURL('image/png');
        } catch {
            return null;
        }
    }, [canvasId]);
    const getMyLayerImage = useCallback(async (wallId = 'concrete') => {
        try {
            const { getCanvasHistory } = await import('./pocketbaseSync');
            const history = await getCanvasHistory(true , wallId);
            if (!history || !history.length) return null;
            const myChunks = history[0].activeChunks;
            if (!myChunks || Object.keys(myChunks).length === 0) return null;
            const W_COLS = 10, W_ROWS = 2;
            const offscreen = document.createElement('canvas');
            offscreen.width  = W_COLS * CHUNK_SIZE;
            offscreen.height = W_ROWS * CHUNK_SIZE;
            const ctx = offscreen.getContext('2d');
            await Promise.all(Object.keys(myChunks).map(chunkId =>
                new Promise(resolve => {
                    const [c, r] = chunkId.split('_').map(Number);
                    const img = new window.Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => { ctx.drawImage(img, c * CHUNK_SIZE, r * CHUNK_SIZE); resolve(); };
                    img.onerror = resolve;
                    img.src = myChunks[chunkId];
                })
            ));
            return trimTransparentCanvas(offscreen);
        } catch {
            return null;
        }
    }, [CHUNK_SIZE]);
    function trimTransparentCanvas(canvas) {
        const ctx = canvas.getContext('2d');
        const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let minX = width, minY = height, maxX = 0, maxY = 0;
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                if (data[(y * width + x) * 4 + 3] > 10) {
                    if (x < minX) minX = x; if (x > maxX) maxX = x;
                    if (y < minY) minY = y; if (y > maxY) maxY = y;
                }
            }
        }
        if (maxX < minX) return canvas;
        const out = document.createElement('canvas');
        out.width  = maxX - minX + 1;
        out.height = maxY - minY + 1;
        out.getContext('2d').drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
        return out;
    }
    const applyHistoryState = useCallback((targetData) => {
        const canvas = document.getElementById(canvasId);
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const chunksSource = targetData.chunksData || targetData.activeChunks;
        if (chunksSource) {
            currentLoadedChunksRef.current = { ...chunksSource };
            Object.keys(chunksSource).forEach(chunkId => {
                const [c, r] = chunkId.split('_').map(Number);
                const img = new window.Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    ctx.drawImage(img, c * CHUNK_SIZE, r * CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE);
                };
                img.src = chunksSource[chunkId];
            });
        } else if (targetData.url) {
            currentLoadedChunksRef.current = {};
            const img = new window.Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
            };
            img.src = targetData.url;
        }
        if (window.updateMinimap) window.updateMinimap();
    }, [canvasId]);
    const redo = useCallback(async () => {
        const plan = getUserPlan();
        if (plan === 'bomber') {
            const msg = getTranslation(lang || 'en', 'alertPlanRestricted');
            if (window.showToast) window.showToast(msg, '💎');
            return;
        }
        if (undoIndexRef.current <= -1) {
            const msg = getTranslation(lang || 'en', 'alertNothingToRedo');
            if (window.showToast) window.showToast(msg, '⏳');
            return;
        }
        undoIndexRef.current -= 1;
        let targetData = null;
        if (undoIndexRef.current === -1) {
            targetData = initialStateRef.current;
        } else {
            targetData = historyRef.current[undoIndexRef.current];
        }
        if (!targetData) return;
        applyHistoryState(targetData);
    }, [lang, applyHistoryState]);
    const undo = useCallback(async () => {
        const plan = getUserPlan();
        if (plan === 'bomber') {
            const msg = getTranslation(lang || 'en', 'alertPlanRestricted');
            if (window.showToast) window.showToast(msg, '💎');
            return;
        }
        try {
            const { getCanvasHistory } = await import('./pocketbaseSync');
            if (historyRef.current.length === 0) {
                historyRef.current = await getCanvasHistory();
                undoIndexRef.current = -1;
            }
            const msg = getTranslation(lang || 'en', 'alertNothingToUndo');
            if (historyRef.current.length === 0) {
                if (window.showToast) window.showToast(msg, '⏳');
                else alert(msg);
                return;
            }
            const nextIndex = undoIndexRef.current + 1;
            if (nextIndex >= historyRef.current.length) {
                if (window.showToast) window.showToast(msg, '📜');
                else alert(msg);
                return;
            }
            if (undoIndexRef.current === -1) {
                const canvas = document.getElementById(canvasId);
                initialStateRef.current = {
                    chunksData: { ...currentLoadedChunksRef.current },
                    url: canvas ? canvas.toDataURL('image/png') : null
                };
            }
            undoIndexRef.current = nextIndex;
            const targetVersion = historyRef.current[nextIndex];
            applyHistoryState(targetVersion);
        } catch (e) {
        }
    }, [canvasId, lang, applyHistoryState]);
    const saveCanvas = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        const canvas = document.getElementById(canvasId);
        if (!canvas) return;
        try {
            const dirtyChunks = Array.from(pendingDirtyChunksRef.current);
            pendingDirtyChunksRef.current.clear();
            let currentChunks = currentLoadedChunksRef.current || {};
            const forceAll = dirtyChunks.length === 0 && Object.keys(currentChunks).length === 0;
            const extractList = forceAll ?
                Array.from({ length: COLS * ROWS }, (_, i) => `${i % COLS}_${Math.floor(i / COLS)}`) :
                dirtyChunks;
            if (extractList.length > 0) {
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = CHUNK_SIZE;
                tempCanvas.height = CHUNK_SIZE;
                const tempCtx = tempCanvas.getContext('2d');
                for (const chunkId of extractList) {
                    const [c, r] = chunkId.split('_').map(Number);
                    if (c >= COLS || r >= ROWS) continue;
                    tempCtx.clearRect(0, 0, CHUNK_SIZE, CHUNK_SIZE);
                    tempCtx.drawImage(
                        canvas,
                        c * CHUNK_SIZE, r * CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE,
                        0, 0, CHUNK_SIZE, CHUNK_SIZE
                    );
                    currentChunks[chunkId] = tempCanvas.toDataURL('image/webp', 0.95);
                }
                currentLoadedChunksRef.current = currentChunks;
            }
            const thumbCanvas = document.createElement('canvas');
            thumbCanvas.width = 400;
            thumbCanvas.height = 160;
            thumbCanvas.getContext('2d').drawImage(canvas, 0, 0, 400, 160);
            const thumbData = thumbCanvas.toDataURL('image/webp', 0.8);
            historyRef.current = isGlobal ? [] : [];
            undoIndexRef.current = -1;
            initialStateRef.current = null;
            import('./pocketbaseSync').then(({ syncCanvasToDB }) => {
                syncCanvasToDB(currentChunks, thumbData, isGlobal);
            });
        } catch (e) {
        }
    }, [canvasId]);
    const notifyStroke = useCallback((dirtyArray) => {
        if (dirtyArray && (Array.isArray(dirtyArray) || (dirtyArray instanceof Set))) {
            dirtyArray.forEach(chunk => pendingDirtyChunksRef.current.add(chunk));
        }
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        let delay = SAVE_COOLDOWN_MS;
        if (dirtyArray && dirtyArray.length > 0) {
            delay = isGlobal ? 5000 : 800;
        }
        debounceTimerRef.current = setTimeout(saveCanvas, delay);
    }, [saveCanvas, isGlobal, SAVE_COOLDOWN_MS]);
    useEffect(() => {
        if (!isGlobal || !engineReady) return;
        let unsubscribe;
        async function setupSubscription() {
            try {
                const { pb, getAnonId } = await import('../../../lib/pb');
                const { GLOBAL_WALL_AUTHOR } = await import('./pocketbaseSync');
                const myId = getAnonId();
                unsubscribe = await pb.collection('canvases').subscribe('*', (e) => {
                    if (e.action === 'create' && e.record.author_id === GLOBAL_WALL_AUTHOR) {
                        const now = Date.now();
                        if (now - lastUpdateTimestampRef.current < UPDATE_COOLDOWN_MS) {
                            return;
                        }
                        if (e.record.chunks_data) {
                            lastUpdateTimestampRef.current = now;
                            const ctx = document.getElementById(canvasId)?.getContext('2d');
                            if (!ctx) return;
                            Object.keys(e.record.chunks_data).forEach(chunkId => {
                                if (currentLoadedChunksRef.current[chunkId] !== e.record.chunks_data[chunkId]) {
                                    const [c, r] = chunkId.split('_').map(Number);
                                    const img = new window.Image();
                                    img.crossOrigin = "anonymous";
                                    img.onload = () => {
                                        ctx.drawImage(img, c * CHUNK_SIZE, r * CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE);
                                        if (window.updateMinimap) window.updateMinimap();
                                    };
                                    img.src = e.record.chunks_data[chunkId];
                                }
                            });
                            currentLoadedChunksRef.current = { ...e.record.chunks_data };
                        }
                    }
                });
            } catch (e) {
            }
        }
        setupSubscription();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [canvasId, engineReady, isGlobal]);
    useEffect(() => {
        if (!engineReady) return;
        window.onSprayStroke = notifyStroke;
        window.onSprayEnd = notifyStroke;
        window.onSprayUndo = isGlobal ? null : undo;
        window.onSprayRedo = isGlobal ? null : redo;
        window.performInstantWallSave = saveCanvas;
        return () => {
            window.onSprayStroke = null;
            window.onSprayEnd = null;
            window.onSprayUndo = null;
            window.onSprayRedo = null;
            window.performInstantWallSave = null;
        };
    }, [engineReady, notifyStroke, undo, redo, saveCanvas, isGlobal]);
    const clearPersistedCanvas = useCallback(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        try {
            pendingDirtyChunksRef.current.clear();
            currentLoadedChunksRef.current = {};
            historyRef.current = [];
            undoIndexRef.current = -1;
            initialStateRef.current = null;
        } catch { }
    }, []);
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);
    return { notifyStroke, clearPersistedCanvas, undo, redo, getGluedImage, getMyLayerImage };
}
